import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Download, Loader2, X, CheckCircle } from 'lucide-react';
import Layout from '../components/Layout';
import PurchaseFormDialog from '../components/PurchaseFormDialog';
import SyncProductsButton from '../components/SyncProductsButton';
import purchaseService from '../services/purchaseService';

const PurchaseFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const mode = id ? 'edit' : 'create';

  const [loading, setLoading] = useState(mode === 'edit' && !location.state?.orderData);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(location.state?.orderData || null);
  const [notification, setNotification] = useState({ open: false, severity: 'success', message: '' });

  const loadOrder = useCallback(async () => {
    if (mode !== 'edit' || !id || order) return;
    try {
      setLoading(true);
      const resp = await purchaseService.getPurchaseOrder(id);
      setOrder(resp);
    } catch (err) {
      setError(err?.response?.data?.error || '加载采购订单失败');
    } finally {
      setLoading(false);
    }
  }, [id, mode, order]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleClose = () => {
    if (mode === 'edit') {
      navigate(`/purchases/${id}/view`);
    } else {
      navigate('/purchases');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (mode === 'edit') {
        await purchaseService.updatePurchaseOrder(id, formData);
        setNotification({ open: true, severity: 'success', message: '采购单已更新' });
        navigate(`/purchases/${id}/view`);
      } else {
        // Use the 'new' endpoint for creating purchase orders
        const created = await purchaseService.createPurchaseOrder(formData);
        setNotification({ open: true, severity: 'success', message: '采购单已创建' });
        // Use the response to navigate to the detail page
        const newId = created?.purchase_order?._id || created?._id || created?.order_id;
        if (newId) {
          navigate(`/purchases/${newId}/view`);
        } else {
          navigate('/purchases');
        }
      }
    } catch (err) {
      setError(err?.response?.data?.error || '保存采购单失败');
      throw err; // let dialog show inline errors if needed
    }
  };

  const handleApprove = async () => {
    try {
      await purchaseService.approvePurchaseOrder(id);
      setNotification({ open: true, severity: 'success', message: '采购单已审批通过' });
      navigate(`/purchases/${id}/view`);
    } catch (err) {
      setError(err?.response?.data?.error || '审批失败');
    }
  };


  const handleExport = async () => {
    try {
      if (!id) return;
      const blob = await purchaseService.exportPurchaseOrderToExcel(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `PO_${order?.purchase_order_id || id}_${timestamp}.xlsx`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setNotification({ open: true, severity: 'success', message: '采购单导出成功' });
    } catch (err) {
      console.error('Export error:', err);
      setError(err?.response?.data?.error || '导出采购单失败');
    }
  };

  // Auto-hide notification after 2.5 seconds
  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [notification.open]);

  if (loading) {
    return (
      <Layout title={mode === 'edit' ? '编辑采购单' : '新建采购单'} activeTab="purchases">
        <div className="max-w-3xl mx-auto py-6 px-4">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={mode === 'edit' ? '编辑采购单' : '新建采购单'} activeTab="purchases">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <PurchaseFormDialog
          open={true}
          onClose={handleClose}
          onSubmit={handleSubmit}
          initialData={mode === 'edit' ? order : null}
        />

        {/* Fixed actions overlay so buttons are visible above the dialog */}
        {mode === 'edit' && (
          <div className="fixed top-[88px] right-8 flex gap-2 z-[1400]">
            {order && order.products && order.products.length > 0 && (
              <SyncProductsButton
                onPreview={async () => {
                  // Preview changes (NO database modification)
                  if (!id) throw new Error('订单ID未定义');
                  const preview = await purchaseService.previewSyncPurchaseOrderProducts(id);
                  return preview;
                }}
                onApply={async () => {
                  // Apply changes after user approval
                  if (!id) throw new Error('订单ID未定义');
                  const result = await purchaseService.syncPurchaseOrderProducts(id);
                  // Reload the order to show updated products
                  if (result.updated_count > 0) {
                    const updatedOrder = await purchaseService.getPurchaseOrder(id);
                    setOrder(updatedOrder);
                    setNotification({ open: true, severity: 'success', message: `成功同步 ${result.updated_count} 个产品` });
                  }
                  return result;
                }}
                variant="outlined"
                size="medium"
              />
            )}
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={!id}
            >
              <Download className="h-4 w-4 mr-2" />
              导出
            </Button>
            {order && (order.approval_status === 'pending' || order.status === 'draft') && (
              <Button
                variant="default"
                className="bg-success hover:bg-success/90"
                onClick={handleApprove}
                disabled={!id}
              >
                审批通过
              </Button>
            )}
          </div>
        )}

        {/* Toast Notification */}
        {notification.open && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
            <Alert className={
              notification.severity === 'success'
                ? 'bg-success text-success-foreground border-success shadow-lg'
                : notification.severity === 'error'
                ? 'bg-destructive text-destructive-foreground border-destructive shadow-lg'
                : 'bg-info text-info-foreground border-info shadow-lg'
            }>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4">
                <span className="font-medium">{notification.message}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setNotification(prev => ({ ...prev, open: false }))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PurchaseFormPage;
